import { useState, useEffect, useRef, useLayoutEffect } from "react";
import {
  insightsApi,
  type ActivityInsightResponse,
  type InsightStatus,
} from "../../lib/api";
import { useChatStore } from "../../stores/chatStore";

// Icons (using outline/line style per project conventions)
function SparklesIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
      />
    </svg>
  );
}

function RefreshIcon({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg
      className="w-4 h-4 text-green-500 flex-shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function LightBulbIcon() {
  return (
    <svg
      className="w-4 h-4 text-yellow-500 flex-shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
      />
    </svg>
  );
}

const EFFORT_COLORS = {
  easy: "bg-green-100 text-green-800",
  moderate: "bg-blue-100 text-blue-800",
  hard: "bg-orange-100 text-orange-800",
  very_hard: "bg-red-100 text-red-800",
};

const EFFORT_LABELS = {
  easy: "Easy",
  moderate: "Moderate",
  hard: "Hard",
  very_hard: "Very Hard",
};

function ClockIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

interface InsightsPanelProps {
  activityId: string;
  provider?: string;
  insightStatus?: InsightStatus;
  onStatusChange?: (status: InsightStatus) => void;
}

const POLL_INTERVAL_MS = 30_000; // 30 seconds

export function InsightsPanel({
  activityId,
  provider = "garmin",
  insightStatus,
  onStatusChange,
}: InsightsPanelProps) {
  const { clearLocalSession } = useChatStore();
  const [insight, setInsight] = useState<ActivityInsightResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkedCache, setCheckedCache] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(insightStatus);
  const scrollPositionRef = useRef<number | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Sync external status prop changes
  useEffect(() => {
    setCurrentStatus(insightStatus);
  }, [insightStatus]);

  // Restore scroll position after insight renders (prevents page jump)
  useLayoutEffect(() => {
    if (scrollPositionRef.current !== null) {
      window.scrollTo(0, scrollPositionRef.current);
      scrollPositionRef.current = null;
    }
  }, [insight]);

  // Load cached insights on mount
  useEffect(() => {
    const loadCached = async () => {
      try {
        const data = await insightsApi.getCached(activityId, provider);
        setInsight(data);
      } catch {
        // 404 or error means no cached insight
      } finally {
        setCheckedCache(true);
      }
    };
    loadCached();
  }, [activityId, provider]);

  // Poll for status changes when insight is being generated in background
  useEffect(() => {
    const shouldPoll =
      !insight &&
      (currentStatus === "pending" || currentStatus === "processing");

    if (!shouldPoll) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    const checkStatus = async () => {
      try {
        // Try to fetch the cached insight directly
        const data = await insightsApi.getCached(activityId, provider);
        if (data) {
          scrollPositionRef.current = window.scrollY;
          setInsight(data);
          setCurrentStatus("completed");
          onStatusChange?.("completed");
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
      } catch {
        // Not ready yet, keep polling
      }
    };

    pollIntervalRef.current = setInterval(checkStatus, POLL_INTERVAL_MS);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [activityId, provider, currentStatus, insight, onStatusChange]);

  const generateInsights = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await insightsApi.get(activityId, { provider, refresh: false });
      scrollPositionRef.current = window.scrollY;
      setInsight(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to generate insights"
      );
    } finally {
      setLoading(false);
    }
  };

  const regenerateInsights = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await insightsApi.get(activityId, { provider, refresh: true });
      scrollPositionRef.current = window.scrollY;
      setInsight(data);
      clearLocalSession(activityId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to generate insights"
      );
    } finally {
      setLoading(false);
    }
  };

  // Still checking cache
  if (!checkedCache) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-3">
          <div className="animate-pulse w-5 h-5 bg-gray-200 rounded" />
          <span className="text-gray-400">Checking for insights...</span>
        </div>
      </div>
    );
  }

  // Loading state (manual generation in progress)
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-3">
          <div className="animate-spin">
            <SparklesIcon />
          </div>
          <span className="text-gray-600">Analyzing your run...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-lg border border-red-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-red-800">Analysis Failed</h2>
            <p className="text-sm text-red-600">{error}</p>
          </div>
          <button
            type="button"
            onClick={generateInsights}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Background processing states (pending/processing) — no insight yet
  if (!insight && (currentStatus === "pending" || currentStatus === "processing")) {
    const isPending = currentStatus === "pending";
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-3">
          {isPending ? (
            <ClockIcon className="w-5 h-5 text-amber-500" />
          ) : (
            <div className="animate-spin">
              <SparklesIcon className="w-5 h-5 text-brand-500" />
            </div>
          )}
          <div>
            <h2 className="text-lg font-medium text-gray-900">AI Insights</h2>
            <p className="text-sm text-gray-500">
              {isPending
                ? "Insights scheduled — will be generated shortly"
                : "Analyzing your run..."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // No insight and no background processing — show generate button
  if (!insight) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SparklesIcon className="w-5 h-5 text-gray-400" />
            <div>
              <h2 className="text-lg font-medium text-gray-900">AI Insights</h2>
              <p className="text-sm text-gray-500">
                Generate AI-powered analysis for this activity
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={generateInsights}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            <SparklesIcon className="w-4 h-4" />
            Generate Insights
          </button>
        </div>
      </div>
    );
  }

  // Insights display
  const { trainingAnalysis, coachingSuggestions, sessionComparison } = insight;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SparklesIcon />
          <h2 className="text-lg font-medium text-gray-900">AI Insights</h2>
          {insight?.cached && (
            <span className="text-xs text-gray-400">(cached)</span>
          )}
        </div>
        <button
          type="button"
          onClick={regenerateInsights}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          title="Regenerate insights"
        >
          <RefreshIcon />
          Refresh
        </button>
      </div>

      {/* Training Analysis */}
      {trainingAnalysis && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900">Training Analysis</h3>
            <span
              className={`px-2 py-1 text-xs font-medium rounded ${EFFORT_COLORS[trainingAnalysis.effortLevel]}`}
            >
              {EFFORT_LABELS[trainingAnalysis.effortLevel]}
            </span>
          </div>

          <p className="text-gray-700">{trainingAnalysis.summary}</p>

          {trainingAnalysis.paceConsistency && (
            <div>
              <h4 className="text-sm font-medium text-gray-600 mb-1">Pace</h4>
              <p className="text-sm text-gray-700">
                {trainingAnalysis.paceConsistency}
              </p>
            </div>
          )}

          {trainingAnalysis.heartRateAnalysis && (
            <div>
              <h4 className="text-sm font-medium text-gray-600 mb-1">
                Heart Rate
              </h4>
              <p className="text-sm text-gray-700">
                {trainingAnalysis.heartRateAnalysis}
              </p>
            </div>
          )}

          {trainingAnalysis.strengths.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-600 mb-2">
                Strengths
              </h4>
              <ul className="space-y-1">
                {trainingAnalysis.strengths.map((strength, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm text-gray-700"
                  >
                    <CheckCircleIcon />
                    <span>{strength}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {trainingAnalysis.areasForImprovement.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-600 mb-2">
                Areas for Improvement
              </h4>
              <ul className="space-y-1">
                {trainingAnalysis.areasForImprovement.map((area, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm text-gray-700"
                  >
                    <LightBulbIcon />
                    <span>{area}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Session Comparison */}
      {sessionComparison && (
        <div className="space-y-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900">Session Comparison</h3>
            <span className="px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-800 capitalize">
              {sessionComparison.category}
            </span>
          </div>

          <p className="text-sm text-gray-600">
            Compared to {sessionComparison.comparedTo} previous {sessionComparison.category} runs
          </p>

          {/* Trends Table */}
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">This Run</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Average</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Trend</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sessionComparison.trends.pace && (
                  <tr>
                    <td className="px-3 py-2 text-sm text-gray-900">Pace</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{sessionComparison.trends.pace.current}</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{sessionComparison.trends.pace.average}</td>
                    <td className="px-3 py-2 text-sm font-medium text-gray-900">{sessionComparison.trends.pace.trend}</td>
                  </tr>
                )}
                {sessionComparison.trends.heartRate && (
                  <tr>
                    <td className="px-3 py-2 text-sm text-gray-900">Heart Rate</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{sessionComparison.trends.heartRate.current}</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{sessionComparison.trends.heartRate.average}</td>
                    <td className="px-3 py-2 text-sm font-medium text-gray-900">{sessionComparison.trends.heartRate.trend}</td>
                  </tr>
                )}
                {sessionComparison.trends.distance && (
                  <tr>
                    <td className="px-3 py-2 text-sm text-gray-900">Distance</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{sessionComparison.trends.distance.current}</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{sessionComparison.trends.distance.average}</td>
                    <td className="px-3 py-2 text-sm font-medium text-gray-900">{sessionComparison.trends.distance.trend}</td>
                  </tr>
                )}
                {sessionComparison.trends.trainingEffect && (
                  <tr>
                    <td className="px-3 py-2 text-sm text-gray-900">Training Effect</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{sessionComparison.trends.trainingEffect.current}</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{sessionComparison.trends.trainingEffect.average}</td>
                    <td className="px-3 py-2 text-sm font-medium text-gray-900">{sessionComparison.trends.trainingEffect.trend}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Insight */}
          <p className="text-sm text-gray-700 italic">{sessionComparison.insight}</p>
        </div>
      )}

      {/* Coaching Suggestions */}
      {coachingSuggestions && (
        <div className="space-y-4 pt-4 border-t border-gray-100">
          <h3 className="font-medium text-gray-900">Coaching Suggestions</h3>

          {coachingSuggestions.forNextRun.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-600 mb-2">
                For Your Next Run
              </h4>
              <ul className="space-y-1">
                {coachingSuggestions.forNextRun.map((suggestion, i) => (
                  <li
                    key={i}
                    className="text-sm text-gray-700 pl-4 border-l-2 border-brand-300"
                  >
                    {suggestion}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {coachingSuggestions.recoveryAdvice && (
            <div>
              <h4 className="text-sm font-medium text-gray-600 mb-1">
                Recovery
              </h4>
              <p className="text-sm text-gray-700">
                {coachingSuggestions.recoveryAdvice}
              </p>
            </div>
          )}

          {coachingSuggestions.weeklyRecommendations &&
            coachingSuggestions.weeklyRecommendations.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-600 mb-2">
                  Weekly Training Tips
                </h4>
                <ul className="space-y-1">
                  {coachingSuggestions.weeklyRecommendations.map((rec, i) => (
                    <li
                      key={i}
                      className="text-sm text-gray-700 pl-4 border-l-2 border-gray-200"
                    >
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
        </div>
      )}

    </div>
  );
}
